import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * HomeMaintenanceAnimation - 家庭维护清单动画
 * 
 * 展示日常、周度、月度维护任务
 * 可视化清洁、检查、保养流程
 * 
 * 适用场景：
 * - 生活百科
 * - 新婚家庭指南
 * - 家务管理教程
 * 
 * 教育价值评分：9/10
 * - 建立家庭维护意识
 * - 系统化家务管理
 */

// 维护任务类型
type Frequency = 'daily' | 'weekly' | 'monthly';

interface MaintenanceTask {
  icon: string;
  name: string;
  nameEn: string;
  frequency: Frequency;
  color: string;
}

// 维护任务数据
const maintenanceTasks: MaintenanceTask[] = [
  // 每日任务
  { icon: '🛏️', name: '整理床铺', nameEn: 'Make Bed', frequency: 'daily', color: '#4A90E2' },
  { icon: '🍽️', name: '清洗碗筷', nameEn: 'Wash Dishes', frequency: 'daily', color: '#4A90E2' },
  { icon: '🗑️', name: '倒垃圾', nameEn: 'Take Out Trash', frequency: 'daily', color: '#4A90E2' },
  { icon: '🌿', name: '浇花浇水', nameEn: 'Water Plants', frequency: 'daily', color: '#4A90E2' },
  
  // 每周任务
  { icon: '🧹', name: '扫地拖地', nameEn: 'Sweep & Mop', frequency: 'weekly', color: '#F59E0B' },
  { icon: '🧺', name: '洗衣晾晒', nameEn: 'Laundry', frequency: 'weekly', color: '#F59E0B' },
  { icon: '🚿', name: '清洁浴室', nameEn: 'Clean Bathroom', frequency: 'weekly', color: '#F59E0B' },
  { icon: '❄️', name: '清理冰箱', nameEn: 'Clean Fridge', frequency: 'weekly', color: '#F59E0B' },
  
  // 每月任务
  { icon: '🪟', name: '擦洗窗户', nameEn: 'Clean Windows', frequency: 'monthly', color: '#10B981' },
  { icon: '🛋️', name: '清洁家具', nameEn: 'Clean Furniture', frequency: 'monthly', color: '#10B981' },
  { icon: '🔧', name: '检查设备', nameEn: 'Check Appliances', frequency: 'monthly', color: '#10B981' },
  { icon: '🔥', name: '清理烟道', nameEn: 'Clean Vents', frequency: 'monthly', color: '#10B981' },
];

// 频率配置
const frequencyConfig = {
  daily: {
    label: '每日',
    labelEn: 'Daily',
    color: '#4A90E2',
    bgGradient: 'linear-gradient(135deg, rgba(74, 144, 226, 0.15), rgba(74, 144, 226, 0.05))',
  },
  weekly: {
    label: '每周',
    labelEn: 'Weekly',
    color: '#F59E0B',
    bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05))',
  },
  monthly: {
    label: '每月',
    labelEn: 'Monthly',
    color: '#10B981',
    bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.05))',
  },
};

// 任务卡片组件
const TaskCard: React.FC<{
  task: MaintenanceTask;
  index: number;
  frame: number;
  fps: number;
  categoryIndex: number;
}> = ({ task, index, frame, fps, categoryIndex }) => {
  const delay = 30 + categoryIndex * 50 + index * 12;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  const x = interpolate(progress, [0, 1], [-30, 0]);

  if (frame < delay) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 20px',
        background: 'rgba(255, 255, 255, 0.04)',
        borderRadius: 12,
        border: `1px solid ${task.color}20`,
        opacity: progress,
        transform: `translateX(${x}px)`,
      }}
    >
      {/* 复选框 */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          border: `2px solid ${task.color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: progress > 0.8 ? `${task.color}30` : 'transparent',
        }}
      >
        {progress > 0.8 && (
          <span style={{ fontSize: 23, color: task.color }}>✓</span>
        )}
      </div>

      {/* 图标 */}
      <span style={{ fontSize: 36 }}>{task.icon}</span>

      {/* 任务名称 */}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 30, fontWeight: 500, color: '#fff' }}>
          {task.name}
        </div>
        <div style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.5)' }}>
          {task.nameEn}
        </div>
      </div>

      {/* 频率标签 */}
      <div
        style={{
          padding: '4px 12px',
          background: `${task.color}20`,
          borderRadius: 6,
          fontSize: 24,
          fontWeight: 500,
          color: task.color,
        }}
      >
        {frequencyConfig[task.frequency].label}
      </div>
    </div>
  );
};

// 频率分类组件
const FrequencySection: React.FC<{
  frequency: Frequency;
  tasks: MaintenanceTask[];
  index: number;
  frame: number;
  fps: number;
  position: { left: number; top: number };
}> = ({ frequency, tasks, index, frame, fps, position }) => {
  const config = frequencyConfig[frequency];
  
  const headerProgress = spring({
    frame: frame - (index * 50),
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  return (
    <div
      style={{
        position: 'absolute',
        left: position.left,
        top: position.top,
        width: 560,
      }}
    >
      {/* 分类标题 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 24,
          opacity: headerProgress,
          transform: `translateX(${(1 - headerProgress) * 30}px)`,
        }}
      >
        {/* 频率图标 */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: config.bgGradient,
            border: `2px solid ${config.color}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 20px ${config.color}20`,
          }}
        >
          <span style={{ fontSize: 32 }}>
            {frequency === 'daily' ? '☀️' : frequency === 'weekly' ? '📅' : '🗓️'}
          </span>
        </div>

        {/* 频率信息 */}
        <div>
          <div style={{ fontSize: 36, fontWeight: 700, color: config.color }}>
            {config.label}
          </div>
          <div style={{ fontSize: 26, color: 'rgba(255, 255, 255, 0.5)' }}>
            {config.labelEn}
          </div>
        </div>

        {/* 任务计数 */}
        <div
          style={{
            marginLeft: 'auto',
            padding: '8px 16px',
            background: `${config.color}15`,
            borderRadius: 8,
          }}
        >
          <span style={{ fontSize: 26, fontWeight: 600, color: config.color }}>
            {tasks.length} 项
          </span>
        </div>
      </div>

      {/* 任务列表 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {tasks.map((task, taskIndex) => (
          <TaskCard
            key={task.name}
            task={task}
            index={taskIndex}
            frame={frame}
            fps={fps}
            categoryIndex={index}
          />
        ))}
      </div>
    </div>
  );
};

// 进度圆环组件
const ProgressRing: React.FC<{
  frequency: Frequency;
  completed: number;
  total: number;
  frame: number;
  fps: number;
  position: { left: number; top: number };
}> = ({ frequency, completed, total, frame, fps, position }) => {
  const config = frequencyConfig[frequency];
  
  const progress = spring({
    frame: frame - 150,
    fps,
    config: { damping: 12, stiffness: 80 },
  });

  const percentage = completed / total;
  const angle = percentage * 360 * progress;
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percentage * progress);

  if (frame < 150) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: position.left,
        top: position.top,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        opacity: progress,
      }}
    >
      <svg width={100} height={100}>
        {/* 背景圆环 */}
        <circle
          cx={50}
          cy={50}
          r={radius}
          stroke={`${config.color}20`}
          strokeWidth={8}
          fill="none"
        />
        {/* 进度圆环 */}
        <circle
          cx={50}
          cy={50}
          r={radius}
          stroke={config.color}
          strokeWidth={8}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 50 50)"
          style={{
            filter: `drop-shadow(0 0 8px ${config.color}60)`,
          }}
        />
        {/* 中心文字 */}
        <text
          x={50}
          y={48}
          textAnchor="middle"
          fill={config.color}
          fontSize={22}
          fontWeight={700}
        >
          {Math.round(percentage * 100 * progress)}%
        </text>
        <text
          x={50}
          y={72}
          textAnchor="middle"
          fill="rgba(255,255,255,0.6)"
          fontSize={20}
        >
          {config.label}
        </text>
      </svg>
    </div>
  );
};

// 顶部装饰性房屋图标
const HouseIcon: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const progress = spring({
    frame: frame - 10,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  const bounce = spring({
    frame: frame - 60,
    fps,
    config: { damping: 8, stiffness: 200 },
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: 60,
        right: 80,
        opacity: progress,
        transform: `scale(${0.8 + bounce * 0.2})`,
      }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2), rgba(16, 185, 129, 0.2))',
          borderRadius: 20,
          border: '2px solid rgba(74, 144, 226, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(74, 144, 226, 0.2)',
        }}
      >
        <span style={{ fontSize: 44 }}>🏠</span>
      </div>
    </div>
  );
};

export const HomeMaintenanceAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 标题动画
  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const titleY = interpolate(titleProgress, [0, 1], [-50, 0]);

  // 按频率分组任务
  const dailyTasks = maintenanceTasks.filter((t) => t.frequency === 'daily');
  const weeklyTasks = maintenanceTasks.filter((t) => t.frequency === 'weekly');
  const monthlyTasks = maintenanceTasks.filter((t) => t.frequency === 'monthly');

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f1629 100%)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* 装饰性背景 */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          background: 'radial-gradient(circle, rgba(74, 144, 226, 0.06), transparent)',
          borderRadius: '50%',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -200,
          left: -200,
          width: 600,
          height: 600,
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06), transparent)',
          borderRadius: '50%',
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 80,
          opacity: titleProgress,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4A90E2 0%, #10B981 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          家庭维护清单
        </h1>
        <p
          style={{
            fontSize: 28,
            margin: '12px 0 0 0',
            color: 'rgba(255, 255, 255, 0.6)',
          }}
        >
          Home Maintenance Checklist · 让家务井井有条
        </p>
      </div>

      {/* 房屋图标 */}
      <HouseIcon frame={frame} fps={fps} />

      {/* 每日任务 */}
      <FrequencySection
        frequency="daily"
        tasks={dailyTasks}
        index={0}
        frame={frame}
        fps={fps}
        position={{ left: 80, top: 260 }}
      />

      {/* 每周任务 */}
      <FrequencySection
        frequency="weekly"
        tasks={weeklyTasks}
        index={1}
        frame={frame}
        fps={fps}
        position={{ left: 680, top: 260 }}
      />

      {/* 每月任务 */}
      <FrequencySection
        frequency="monthly"
        tasks={monthlyTasks}
        index={2}
        frame={frame}
        fps={fps}
        position={{ left: 1280, top: 260 }}
      />

      {/* 进度统计 */}
      <ProgressRing
        frequency="daily"
        completed={4}
        total={4}
        frame={frame}
        fps={fps}
        position={{ left: 80, top: 880 }}
      />
      <ProgressRing
        frequency="weekly"
        completed={3}
        total={4}
        frame={frame}
        fps={fps}
        position={{ left: 680, top: 880 }}
      />
      <ProgressRing
        frequency="monthly"
        completed={2}
        total={4}
        frame={frame}
        fps={fps}
        position={{ left: 1280, top: 880 }}
      />

      {/* 底部提示 */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 80,
          right: 80,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          opacity: interpolate(frame, [200, 230], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                background: '#4A90E2',
              }}
            />
            <span style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.6)' }}>
              每日任务
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                background: '#F59E0B',
              }}
            />
            <span style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.6)' }}>
              每周任务
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                background: '#10B981',
              }}
            />
            <span style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.6)' }}>
              每月任务
            </span>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 24px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 12,
          }}
        >
          <span style={{ fontSize: 24 }}>💡</span>
          <span style={{ fontSize: 24, color: '#10B981' }}>
            坚持维护，让家更温馨
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};
